import type { NavigateFunction } from "react-router-dom";

/**
 * Landing anchor navigation — hash links that target sections on the marketing home route (`/`).
 *
 * - **In-page** (`pathname === '/'`): sections exist; use smooth scroll via `scrollToLandingSection`.
 * - **Cross-route** (e.g. `/blog` → `/#pricing`): DOM mounts asynchronously; use `scrollToLandingHashWhenReady`
 *   after navigation (typically from `LandingPage` + `location.hash`) so layout settles before scrolling.
 * - **Programmatic jump + navigate**: `goToLandingSection` picks scroll vs `navigate({ pathname: '/', hash })`.
 */

/** Must stay aligned with `App` `<Route path="/" …>`. */
export const LANDING_HOME_PATH = "/" as const;

const SCROLL = {
  /** In-page nav: marketing preference for smooth motion. */
  inPage: { behavior: "smooth", block: "start" } satisfies ScrollIntoViewOptions,
  /** After SPA route change: deterministic position; avoids fighting layout thrash. */
  afterNavigation: { behavior: "auto", block: "start" } satisfies ScrollIntoViewOptions,
} as const;

const HASH_SCROLL = {
  /** Interval between attempts while waiting for `#id` to exist in the DOM. */
  retryIntervalMs: 48,
  /** ~2.4s total wait before giving up (avoids infinite loops on bad hashes). */
  maxAttempts: 50,
  /** Second `scrollIntoView` after paint so images / motion can settle. */
  layoutSettleDelayMs: 220,
} as const;


/**
 * Returns `#foo` from `#foo` or `foo`. Empty / whitespace → empty string.
 */
export function normalizeLandingHash(hash: string): string {
  const t = hash.trim();
  if (!t) return "";
  return t.startsWith("#") ? t : `#${t}`;
}

/** Element id without `#`, for `getElementById`. */
function hashToElementId(hash: string): string {
  const normalized = normalizeLandingHash(hash);
  return normalized.startsWith("#") ? normalized.slice(1) : normalized;
}

export function isLandingHomePath(pathname: string): boolean {
  return pathname === LANDING_HOME_PATH;
}

/**
 * Smooth scroll to a landing section when already on `/` and the target node exists.
 */
export function scrollToLandingSection(hash: string): void {
  const id = hashToElementId(hash);
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView(SCROLL.inPage);
}

/**
 * After `navigate({ pathname: '/', hash })`, retries until `#id` exists, scrolls, then scrolls again
 * after `layoutSettleDelayMs` so cross-route positioning matches in-page anchors.
 *
 * @returns Cancel pending timers (e.g. `useEffect` cleanup).
 */
export function scrollToLandingHashWhenReady(hash: string): () => void {
  const id = hashToElementId(hash);
  if (!id) return noop;

  let attempts = 0;
  let retryTimer: number | undefined;
  let settleTimer: number | undefined;

  const clearRetry = () => {
    if (retryTimer !== undefined) {
      window.clearTimeout(retryTimer);
      retryTimer = undefined;
    }
  };

  const clearSettle = () => {
    if (settleTimer !== undefined) {
      window.clearTimeout(settleTimer);
      settleTimer = undefined;
    }
  };

  const cancel = () => {
    clearRetry();
    clearSettle();
  };

  const applyScroll = (el: HTMLElement) => {
    el.scrollIntoView(SCROLL.afterNavigation);
  };

  const tryScrollOrRetry = (): void => {
    const el = document.getElementById(id);
    if (el) {
      applyScroll(el);
      clearSettle();
      settleTimer = window.setTimeout(() => applyScroll(el), HASH_SCROLL.layoutSettleDelayMs);
      return;
    }

    if (attempts >= HASH_SCROLL.maxAttempts) return;
    attempts += 1;
    clearRetry();
    retryTimer = window.setTimeout(tryScrollOrRetry, HASH_SCROLL.retryIntervalMs);
  };

  tryScrollOrRetry();
  return cancel;
}

function noop(): void {}

/**
 * If already on the landing route: smooth-scroll. Otherwise SPA-navigate to `/#fragment` so
 * `LandingPage` mounts and `scrollToLandingHashWhenReady` can align the viewport.
 */
export function goToLandingSection(hash: string, navigate: NavigateFunction, pathname: string): void {
  const normalized = normalizeLandingHash(hash);
  if (!normalized) return;

  if (isLandingHomePath(pathname)) {
    scrollToLandingSection(normalized);
    return;
  }

  navigate({ pathname: LANDING_HOME_PATH, hash: normalized });
}
